/**
 * Slot-Anzeige (Warnungen, Monteur-Wahl, Mini-Tageskalender) — aus
 * ActionPlanCard herausgezogen (01.10.2026), damit der Terminlink im Plan
 * dieselbe Anzeige und denselben Live-Check zeigt wie die Mail-Slots.
 */
import { useEffect, useState } from "react"
import { AlertTriangle } from "lucide-react"
import { MiniDayCalendar, type DayEvent } from "@/components/portal/MiniDayCalendar"
import type { ActionPlan } from "./types"

export type SlotCheck = {
  is_free: boolean
  candidates: { partner_id: number; name: string }[]
  message: string
  day_events?: DayEvent[]
}

/** Live-Check eines Slots: frei? welche Monteure? Tagesansicht.
 *  `start` = naive Berlin-Zeit („2026-10-09T07:00:00“), wie im Plan. */
export function useSlotCheck(quelle: number | { tenant: string }, start: string, durationMin: number,
                             partnerId?: number | null) {
  // Zahl = Mail (Plan-Karte), sonst je Betrieb (Terminlinks-Liste) — dieselbe Pruefung.
  const basis = typeof quelle === "number"
    ? `/api/v1/drafts/${quelle}/slot-check?`
    : `/api/v1/terminlinks/slot-check?tenant=${encodeURIComponent(quelle.tenant)}&`
  const [check, setCheck] = useState<SlotCheck | null>(null)
  useEffect(() => {
    setCheck(null)
    if (!start) return
    const params = new URLSearchParams({ start, duration_min: String(durationMin || 45) })
    if (partnerId) params.set("partner_id", String(partnerId))
    const ctrl = new AbortController()
    fetch(`${basis}${params.toString()}`, { credentials: "include", signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (j) setCheck(j) })
      .catch(() => { /* ignore */ })
    return () => ctrl.abort()
  }, [basis, start, durationMin, partnerId])
  return check
}

/**
 * Was zu einem Terminvorschlag gehoert: Warnungen, Monteur-Wahl, Tagesansicht.
 *
 * Bewusst ein Baustein fuer beide Oberflaechen. Im Posteingang stand hier
 * lange nur Datum und Dauer — man sah weder, ob der Slot noch frei ist, noch
 * wer faehrt, noch dass ein Termin bereits als BLOCKER liegt und vom Kunden
 * bestaetigt wurde. Genau diese drei Dinge entscheiden aber, ob man den Plan
 * ausfuehren will.
 */
export function SlotDetails({
  slot, slotCheck, blockedReason, confirmed = false, onSelectMonteur, reserviert = false,
}: {
  slot: ActionPlan["slot"]
  slotCheck: SlotCheck | null
  blockedReason?: string
  confirmed?: boolean
  onSelectMonteur?: (partner_id: number) => void
  /** Der Slot ist unser eigener BLOCKER (Terminlink) — „belegt“ ist dann
   *  kein Fehler, sondern genau die Reservierung. */
  reserviert?: boolean
}) {
  const candidates = slotCheck?.candidates || []
  const selectedPartnerId = slot.partner_id
  const selectedName = selectedPartnerId
    ? candidates.find((c) => c.partner_id === selectedPartnerId)?.name
    : undefined
  return (
    <>
      {blockedReason && (
        <div style={{
          background: "#fff7ed", color: "#9A3412", border: "0.5px solid #fed7aa",
          padding: "4px 8px", borderRadius: 5, fontSize: 12, fontWeight: 500,
          marginTop: 6, display: "inline-flex", alignItems: "flex-start", gap: 5,
        }}>
          <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 1 }} /> {blockedReason}
        </div>
      )}
      {reserviert && (
        <div style={{
          background: "#EEEDFE", color: "#3C3489", border: "0.5px solid rgba(60,52,137,0.3)",
          padding: "4px 8px", borderRadius: 5, fontSize: 12, fontWeight: 500,
          marginTop: 6, display: "inline-flex", alignItems: "center", gap: 5,
        }}>
          Als BLOCKER im Kalender reserviert
        </div>
      )}
      {!blockedReason && !reserviert && slotCheck && !slotCheck.is_free && !confirmed && (
        <div style={{
          background: "#fee2e2", color: "#991b1b", border: "0.5px solid #fca5a5",
          padding: "4px 8px", borderRadius: 5, fontSize: 12, fontWeight: 500,
          marginTop: 6, display: "inline-flex", alignItems: "center", gap: 5,
        }}>
          <AlertTriangle size={12} /> Slot inzwischen belegt — bitte anderen waehlen
        </div>
      )}
      {!blockedReason && confirmed && (
        <div style={{
          background: "#dcfce7", color: "#166534", border: "0.5px solid #86efac",
          padding: "4px 8px", borderRadius: 5, fontSize: 12, fontWeight: 500,
          marginTop: 6, display: "inline-flex", alignItems: "center", gap: 5,
        }}>
          ✓ Vom Kunden bestaetigt — bestehender Termin wird beim Ausfuehren bestaetigt (kein Doppel-Anlegen)
        </div>
      )}
      {!blockedReason && !reserviert && candidates.length > 0 && (
        <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "#3C3489", fontWeight: 500 }}>👷 Monteur:</span>
          {candidates.map((c) => {
            const isSelected = c.partner_id === selectedPartnerId
            return (
              <button
                key={c.partner_id}
                type="button"
                disabled={!onSelectMonteur}
                onClick={() => onSelectMonteur?.(c.partner_id)}
                style={{
                  background: isSelected ? "#3C3489" : "#fff",
                  color: isSelected ? "#fff" : "#3C3489",
                  border: `0.5px solid ${isSelected ? "#3C3489" : "rgba(60,52,137,0.4)"}`,
                  padding: "2px 8px", borderRadius: 999,
                  fontSize: 12, fontWeight: 500,
                  cursor: onSelectMonteur ? "pointer" : "default",
                }}
              >
                {isSelected ? "✓ " : ""}{c.name}
              </button>
            )
          })}
          {selectedPartnerId && !candidates.some((c) => c.partner_id === selectedPartnerId) && (
            <span style={{ fontSize: 12, color: "#9A3412", fontStyle: "italic" }}>
              (ausgewaehlter Monteur #{selectedPartnerId} {selectedName ? `(${selectedName})` : ""} ist diesen Slot belegt)
            </span>
          )}
        </div>
      )}
      {!blockedReason && slot.start_datetime && slotCheck?.day_events && (
        <MiniDayCalendar
          slotStart={slot.start_datetime}
          slotDurationMin={slot.duration_minutes || 45}
          events={
            // Filter-Logik:
            //  - explizit ausgewaehlter Monteur → nur dessen Termine + firmen-weite
            //  - kein Monteur explizit, aber genau 1 Kandidat frei → filter auf den
            //    (sonst irrelevant viele Termine anderer Monteure im Mini-Kalender)
            //  - mehrere Kandidaten → alle Events anzeigen, User soll selbst waehlen
            (() => {
              const effectivePid = slot.partner_id ??
                (slotCheck.candidates.length === 1 ? slotCheck.candidates[0].partner_id : null)
              if (!effectivePid) return slotCheck.day_events
              return slotCheck.day_events.filter((ev) =>
                !ev.partner_ids?.length || ev.partner_ids.includes(effectivePid)
              )
            })()
          }
          isFree={slotCheck.is_free}
        />
      )}
    </>
  )
}

